import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  MenuItem,
  CircularProgress,
  Alert,
} from "@mui/material";
import api from "../../services/api"; // Assuming api service exists
// import { hrService } from "../../services/leaveService"; // Or use service

interface EditBalanceDialogProps {
  open: boolean;
  onClose: () => void;
  employee: any;
  leaveType: any;
  currentBalance?: any;
  onSuccess: () => void;
}

const EditBalanceDialog: React.FC<EditBalanceDialogProps> = ({
  open,
  onClose,
  employee,
  leaveType,
  currentBalance,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    totalEntitlement: 0,
    usedDays: 0,
    reason: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && currentBalance) {
      setFormData({
        totalEntitlement: currentBalance.total_entitlement || 0,
        usedDays: currentBalance.used_days || 0,
        reason: "",
      });
    } else if (open && leaveType) {
      // If no current balance, initialize with 0
      // Entitlement should be configured via Leave Entitlements
      setFormData({
        totalEntitlement: 0,
        usedDays: 0,
        reason: "",
      });
    }
  }, [open, currentBalance, leaveType]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "reason" ? value : parseFloat(value) || 0,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.reason) {
      setError("Please provide a reason for this adjustment for audit logs.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      await api.post("/hr/balance/update", {
        employeeId: employee.id,
        leaveTypeId: leaveType.id || leaveType.leave_type_id,
        year: new Date().getFullYear(), // Default to current year for now
        totalEntitlement: formData.totalEntitlement,
        usedDays: formData.usedDays,
        reason: formData.reason,
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Failed to update balance:", err);
      setError(err.response?.data?.error || "Failed to update balance");
    } finally {
      setLoading(false);
    }
  };

  const available = formData.totalEntitlement - formData.usedDays;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Leave Balance</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="subtitle2" gutterBottom>
          Employee: <strong>{employee?.name}</strong> ({employee?.employee_code}
          )
        </Typography>
        <Typography variant="subtitle2" gutterBottom sx={{ mb: 3 }}>
          Leave Type:{" "}
          <strong>{leaveType?.name || leaveType?.leave_type_name}</strong>
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Total Entitlement"
              type="number"
              name="totalEntitlement"
              fullWidth
              value={formData.totalEntitlement}
              onChange={handleChange}
              helperText="Max allowed days"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Used Days"
              type="number"
              name="usedDays"
              fullWidth
              value={formData.usedDays}
              onChange={handleChange}
              helperText="Days already taken"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant="body2"
              sx={{
                textAlign: "right",
                fontWeight: "bold",
                color: available < 0 ? "error.main" : "success.main",
              }}
            >
              New Available Balance: {available}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Reason for Adjustment (Required)"
              name="reason"
              fullWidth
              multiline
              rows={3}
              value={formData.reason}
              onChange={handleChange}
              required
              placeholder="e.g. Correction of used days, Policy update, Bonus leave..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : "Update Balance"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditBalanceDialog;
